// Castintech dashboard — Onaylar (Approvals) view modulu.
// Faz 9b PR8 — pending approval listesi + approve/reject.
// Faz 13 — strict TS hardening.

import { state } from '../state.js';
import * as Api from '../api.js';
import { escAttr, $, asEl } from '../utils.js';
import { refresh } from '../main.js';
import { toast } from '../ui/toast.js';
import { renderSidebar } from '../ui/sidebar.js';

export function renderOnaylar(): void {
  const pending = state.approvals.filter(a => a.status === 'pending');
  const sub = $('onaylar-sub');
  if (sub) sub.textContent = pending.length ? pending.length + ' bekleyen onay' : 'Bekleyen onay yok';

  const list = $('approval-list');
  if (!list) return;

  if (!pending.length) {
    list.innerHTML = `
      <div class="empty-state"><div class="empty-icon">✅</div><div class="empty-title">Bekleyen onay yok</div><div class="empty-sub">Forge veya Pilot bir istek gönderdiğinde burada görünür</div></div>`;
    return;
  }

  list.innerHTML = pending.map((ap, i) => `
    <div class="approval-card" style="animation-delay:${i * 60}ms">
      <div class="ap-header">
        <div class="ap-meta">
          <div class="ap-type">${ap.type === 'hire_agent' ? 'Ajan İşe Alımı' : ap.type || 'Onay'}</div>
          <div class="ap-title">${ap.payload?.name || ap.payload?.title || 'İstek'}</div>
        </div>
      </div>
      <div class="ap-detail">${ap.payload?.capabilities || ap.payload?.description || JSON.stringify(ap.payload || {}).substring(0, 120)}</div>
      <div class="ap-actions">
        <button class="btn-approve" data-action="approve" data-approval-id="${escAttr(ap.id)}">✓ Onayla</button>
        <button class="btn-reject" data-action="reject" data-approval-id="${escAttr(ap.id)}">✕ Reddet</button>
      </div>
    </div>`).join('');
}

// M6 hotfix (2026-05-15) — UI refresh after approve/reject.
// PR #334 fixed endpoint URL but UI badge/banner stayed stale because:
//   1. postApprove/postReject yanıtı .ok kontrolü yoktu — 4xx/5xx sessiz geçti.
//   2. refresh() çağrılsa bile central_brain endpoint cache/eventual-consistency
//      arası state.approvals'a aynı item geri gelebiliyor.
// Fix: response.ok kontrol + optimistic update (state.approvals'tan derhal düş)
// + sidebar badge + onaylar sub-text re-render + arka planda refresh() ile
// canonical state senkronu. Hata durumunda toast + state geri yüklenmez (refresh
// canonical kaynağı geri çeker).
async function transitionLocal(
  id: string,
  action: 'approve' | 'reject',
  apiCall: (id: string) => Promise<Response>,
): Promise<void> {
  let r: Response;
  try {
    r = await apiCall(id);
  } catch (e) {
    toast(`${action === 'approve' ? 'Onay' : 'Ret'} hatası: ` + (e as Error).message, 'error');
    return;
  }
  if (!r.ok) {
    // 2026-05-30 fix: 404 = onay sunucuda yok (Paperclip legacy/hayalet kayıt;
    // central_brain'de karşılığı yok veya zaten çözülmüş). Hata gösterip takılı
    // bırakmak yerine listeden düşür (self-heal) + bilgi toast. Diğer hatalar error.
    if (r.status === 404) {
      state.approvals = state.approvals.filter(a => a.id !== id);
      renderSidebar();
      renderOnaylar();
      toast('Bu onay artık mevcut değil (listeden kaldırıldı).', 'info');
      await refresh().catch(() => { /* canonical sync */ });
      return;
    }
    const t = await r.text().catch(() => 'HTTP ' + r.status);
    toast(`${action === 'approve' ? 'Onayla' : 'Reddet'} başarısız: ${t.slice(0, 160)}`, 'error');
    return;
  }
  // Optimistic: pending listesinden derhal düş (hızlı UI tepki).
  state.approvals = state.approvals.filter(a => a.id !== id);
  // Badge + Onaylar sub-text + view re-render (immediate visual update).
  renderSidebar();
  renderOnaylar();
  toast(action === 'approve' ? 'Onaylandı' : 'Reddedildi', 'success');
  // Canonical state için arka planda refresh (await — race önler).
  await refresh();
}

// 2026-05-30 — Kaynak-farkında routing. state.approvals 2 kaynağı merge eder:
//   • Paperclip approvals (companyId'li — REQUEST_BOARD_APPROVAL, hire_agent) → proxy yolu
//   • central_brain approvals (companyId yok) → /api/v2 yolu
// Önceden hepsi /api/v2'ye gidiyordu → Paperclip UUID'leri 404 → modül takılı.
function isPaperclipApproval(id: string): boolean {
  const item = state.approvals.find(a => a.id === id) as { companyId?: string } | undefined;
  return Boolean(item && item.companyId);
}

export async function approveItem(id: string): Promise<void> {
  const fn = isPaperclipApproval(id) ? Api.postApprovePaperclip : Api.postApprove;
  await transitionLocal(id, 'approve', fn);
}

export async function rejectItem(id: string): Promise<void> {
  const fn = isPaperclipApproval(id) ? Api.postRejectPaperclip : Api.postReject;
  await transitionLocal(id, 'reject', fn);
}

// Faz 10 PR4 — Onaylar list delegated click. data-action=approve|reject + data-approval-id.
let _onaylarDelegated = false;
export function bindOnaylarDelegation(): void {
  if (_onaylarDelegated) return;
  const list = $('approval-list');
  if (!list) return;
  list.addEventListener('click', (e: MouseEvent) => {
    const btn = asEl(e.target)?.closest<HTMLElement>('[data-action]');
    if (!btn || !list.contains(btn)) return;
    const id = btn.dataset.approvalId;
    if (!id) return;
    if (btn.dataset.action === 'approve') approveItem(id);
    else if (btn.dataset.action === 'reject') rejectItem(id);
  });
  _onaylarDelegated = true;
}
